import _objectWithoutProperties from "/drone/src/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["className"];
import React, { forwardRef, memo } from 'react';
import { PortalToBody } from '@deliveryhero/armor-system';
import { useDatePickerClassNames } from '../hooks/useDatePickerClassNames';
import { useDatePicker } from './hooks/useDatePicker';
import { DatePickerRoot } from './style';
import { DatePickerInput, DatePickerInputIcon, DatePickerDropdown, DatePickerDropdownArrow } from '../style';
import { DATE_PICKER_CLASS_PREFIX } from '../constants';
import { DatePickerTimeSelector } from '../DatePickerTimeSelector';
import { DatePickerActionBar } from '../DatePickerActionBar';
import { DatePickerMonthYearSelector } from '../DatePickerMonthYearSelector';
import { DatePickerDaySelector } from '../DatePickerDaySelector';
import { DatePickerTopBar } from '../DatePickerTopBar';
/**
 * # DatePicker
 *
 * ## [Documentation](https://armor.deliveryhero.com/251886272/p/0591e9-date-picker/b/35ef35)
 *
 * ## Examples
 *
 * ***
 *
 * ```
 * import { useState } from 'react';
 * import { DatePicker } from '@deliveryhero/armor';
 *
 * const [value, setValue] = useState<Date>();
 *
 * <DatePicker dateValue={value} onDateValueChange={setValue} />
 * ```
 * ***
 *
 * @armor-docs-component
 */
export var DatePicker = forwardRef(function DatePicker(_ref, ref) {
  var className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  var classNameComponents = useDatePickerClassNames(DATE_PICKER_CLASS_PREFIX, className);
  var _useDatePicker = useDatePicker(props, ref),
    portalProps = _useDatePicker.portalProps,
    rootProps = _useDatePicker.rootProps,
    inputProps = _useDatePicker.inputProps,
    inputMaskProps = _useDatePicker.inputMaskProps,
    showMaskedInput = _useDatePicker.showMaskedInput,
    dropdownProps = _useDatePicker.dropdownProps,
    arrowProps = _useDatePicker.arrowProps,
    topBarProps = _useDatePicker.topBarProps,
    daySelectorProps = _useDatePicker.daySelectorProps,
    monthYearSelectorProps = _useDatePicker.monthYearSelectorProps,
    actionBarProps = _useDatePicker.actionBarProps,
    timeSelectorProps = _useDatePicker.timeSelectorProps,
    displayMonthYearSelector = _useDatePicker.displayMonthYearSelector,
    open = _useDatePicker.open,
    showActions = _useDatePicker.showActions,
    showTimePicker = _useDatePicker.showTimePicker,
    onInputIconClick = _useDatePicker.onInputIconClick;
  var enableMonthYearPickerMode = monthYearSelectorProps.enableMonthYearPickerMode;
  return React.createElement(DatePickerRoot, Object.assign({}, rootProps, {
    className: classNameComponents.Root
  }), React.createElement(DatePickerInput, Object.assign({}, showMaskedInput ? inputMaskProps : inputProps, {
    type: "text",
    className: classNameComponents.Input,
    after: React.createElement(DatePickerInputIcon, {
      onClick: onInputIconClick
    }),
    ref: inputProps.ref
  })), open && React.createElement(PortalToBody, Object.assign({}, portalProps), React.createElement(DatePickerDropdown, Object.assign({}, dropdownProps, {
    className: classNameComponents.Dropdown
  }), !enableMonthYearPickerMode && React.createElement(DatePickerTopBar, Object.assign({}, topBarProps)), displayMonthYearSelector && React.createElement(DatePickerMonthYearSelector, Object.assign({}, monthYearSelectorProps)), !displayMonthYearSelector && React.createElement(DatePickerDaySelector, Object.assign({}, daySelectorProps)), showTimePicker && React.createElement(DatePickerTimeSelector, Object.assign({}, timeSelectorProps)), showActions && React.createElement(DatePickerActionBar, Object.assign({}, actionBarProps)), React.createElement(DatePickerDropdownArrow, Object.assign({}, arrowProps)))));
});
DatePicker.defaultProps = {};
/** prop-types are required here for run-time checks */

export var MemoizedDatePicker = memo(DatePicker);